import { useState, useMemo, useEffect, useRef } from 'react';
import { Page } from '@/components/Page';
import { Section } from '@/components/Section';
import { Cell } from '@/components/Cell';
import { Button } from '@/components/Button';
import { Empty } from '@/components/Empty';
import { Spinner } from '@/components/Spinner';
import { SymbolPicker } from '@/components/SymbolPicker';
import { ScrapeQuotesTable } from '@/components/ScrapeQuotesTable';
import { ZetaHistoryChart } from '@/components/ZetaHistoryChart';
import { useSupabaseQuery, sb } from '@/hooks/useSupabaseQuery';
import { useEnsureActiveOptionsFresh } from '@/hooks/useEnsureActiveOptionsFresh';
import { useAuth } from '@/providers/AuthProvider';
import { useHaptic } from '@/hooks/useHaptic';
import { fmtDateTime } from '@/lib/format';
import {
  extractOptionExpiry,
  extractOptionSide,
  fmtOptionExpiry,
} from '@/lib/optionSymbolFilters';
import type { ZetaSnapshot } from '@/types/domain';

/** Таймштамп скрапа с биржей и числом символов. */
interface ScrapeTs {
  taken_at: string;
  exchange: string;
  symbol_count: number;
}

/** Выбранный скрап: время + биржа. */
interface SelectedScrape {
  taken_at: string;
  exchange: string;
}

/** Строка котировки для конкретного скрапа. */
interface ScrapeQuote {
  symbol: string;
  bid: number;
  ask: number;
  taken_at: string;
}

/** Spot-цена базового актива на момент скрапа. */
interface ScrapeUnderlying {
  base: string;
  spot_price: number;
}

interface ZetaAnomalyWatcher {
  id: number;
  user_id: string;
  option_id: string;
  exchange: string;
  expiry_days: number;
  bid_threshold_percent: number | null;
  ask_threshold_percent: number | null;
  created_at: string;
}

interface ZetaAnomalySettings {
  user_id: string;
  global_bid_threshold_percent: number;
  global_ask_threshold_percent: number;
  anomaly_notifications_enabled: boolean;
}

interface ZetaExchangeNotificationSetting {
  user_id: string;
  exchange: string;
  enabled: boolean;
}

interface ZetaTrimmedStats {
  latest_bid: number | null;
  latest_ask: number | null;
  latest_taken_at: string | null;
  avg_bid_trimmed: number | null;
  avg_ask_trimmed: number | null;
  sample_size: number;
  sample_size_bid: number;
  sample_size_ask: number;
}

interface ZetaAnomalyOverviewRow {
  option_id: string;
  expiry: string;
  expiry_days: number;
  exchange: string | null;
  last_bid: number | null;
  last_ask: number | null;
  last_taken_at: string | null;
  avg_bid_trimmed: number | null;
  avg_ask_trimmed: number | null;
  max_bid: number | null;
  max_bid_exchange: string | null;
  max_bid_taken_at: string | null;
  min_ask: number | null;
  min_ask_exchange: string | null;
  min_ask_taken_at: string | null;
}

type ZetaTab = 'symbol' | 'scrapes' | 'anomalies';

/** Человекочитаемое название биржи. */
const EXCHANGE_LABELS: Record<string, string> = {
  gate: 'Gate.io',
  deribit: 'Deribit',
  okx: 'OKX',
  binance: 'Binance',
  bybit: 'Bybit',
  htx: 'HTX',
  mexc: 'MEXC',
  delta: 'Delta Exchange',
  coincall: 'Coincall',
  bingx: 'BingX',
};
const ZETA_FILTER_DEBOUNCE_MS = 300;

function exchangeLabel(exchange: string | null | undefined): string {
  return EXCHANGE_LABELS[exchange ?? ''] ?? exchange ?? '—';
}

/**
 * Значение datetime-local трактуем как московское время (UTC+3) — так же,
 * как время отображается в таблице (см. `src/lib/format.ts`), а не как tz браузера.
 */
function toIsoFromDateTimeLocal(value: string): string | null {
  const m = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(:\d{2}(?:\.\d{1,3})?)?$/.exec(value.trim());
  if (!m) return null;
  const d = new Date(`${m[1]}${m[2] ?? ':00'}+03:00`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function csvCell(value: string | number | null | undefined): string {
  if (value == null) return '';
  const str = String(value);
  return /[",\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
}

/** CSV истории котировок символа (по возрастанию времени). Колонки *_size_base — только если есть данные. */
function buildSymbolHistoryCsv(rows: ZetaSnapshot[]): string {
  const hasBase = rows.some((r) => r.bid_size_base != null || r.ask_size_base != null);
  const header = ['taken_at', 'exchange', 'bid', 'ask', 'bid_size', 'ask_size'];
  if (hasBase) header.push('bid_size_base', 'ask_size_base');
  const sorted = [...rows].sort(
    (a, b) => new Date(a.taken_at).getTime() - new Date(b.taken_at).getTime() || a.exchange.localeCompare(b.exchange),
  );
  const lines = sorted.map((r) => {
    const cells: Array<string | number | null | undefined> = [
      new Date(r.taken_at).toISOString(),
      r.exchange,
      r.bid,
      r.ask,
      r.bid_size,
      r.ask_size,
    ];
    if (hasBase) cells.push(r.bid_size_base, r.ask_size_base);
    return cells.map(csvCell).join(',');
  });
  return [header.join(','), ...lines].join('\r\n');
}

/** Скачивание файла через Blob + object URL (работает и в Telegram WebView на десктопе/Android). */
function downloadTextFile(filename: string, content: string, mime: string): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function daysUntil(iso: string): number {
  const ms = new Date(iso).getTime() - Date.now();
  return ms / 86_400_000;
}

function fmtPrice(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return Number(value).toFixed(8).replace(/\.?0+$/, '');
}

function hasPositivePrice(value: number | null | undefined): boolean {
  return Number(value) > 0;
}

function optionBase(optionId: string): string {
  return optionId.split('-')[0] ?? optionId;
}

function isBidTrimmedHighlight(avgBidTrimmed: number | null | undefined, maxBid: number | null | undefined): boolean {
  if (!(Number(avgBidTrimmed) > 0) || !(Number(maxBid) > 0)) return false;
  return Number(maxBid) > Number(avgBidTrimmed) * 1.5;
}

function isAskTrimmedHighlight(avgAskTrimmed: number | null | undefined, minAsk: number | null | undefined): boolean {
  if (!(Number(avgAskTrimmed) > 0) || !(Number(minAsk) > 0)) return false;
  return Number(minAsk) < Number(avgAskTrimmed) * 0.5;
}

function shouldRenderAnomalyExchangeRow(row: Pick<ZetaAnomalyOverviewRow, 'avg_bid_trimmed' | 'avg_ask_trimmed'>): boolean {
  return row.avg_bid_trimmed != null || row.avg_ask_trimmed != null;
}

const HALF_HOUR_BUCKET_MS = 30 * 60 * 1000;
const MINUTE_BUCKET_MS = 60 * 1000;
const HALF_HOUR_BUCKET_TOLERANCE_MS = 2 * 60 * 1000;

/** Округляет timestamp к получасовому интервалу, если отклонение не больше 2 минут; иначе — к ближайшей минуте. */
function toBucketMs(iso: string): number {
  const ts = new Date(iso).getTime();
  const nearestHalfHour = Math.round(ts / HALF_HOUR_BUCKET_MS) * HALF_HOUR_BUCKET_MS;
  if (Math.abs(ts - nearestHalfHour) <= HALF_HOUR_BUCKET_TOLERANCE_MS) return nearestHalfHour;
  return Math.round(ts / MINUTE_BUCKET_MS) * MINUTE_BUCKET_MS;
}

/** Раздел Zeta. История цен по конкретному символу и полная история скрапов. */
export function ZetaPage(): JSX.Element {
  const { userId } = useAuth();
  const { notify } = useHaptic();
  const [tab, setTab] = useState<ZetaTab>('symbol');
  const [visitedTabs, setVisitedTabs] = useState<Record<ZetaTab, boolean>>({
    symbol: true,
    scrapes: false,
    anomalies: false,
  });
  const [symbol, setSymbol] = useState('');
  const [symbolSearchInput, setSymbolSearchInput] = useState('');
  const [symbolSideFilter, setSymbolSideFilter] = useState<'all' | 'C' | 'P'>('all');
  const [symbolExpiryFilter, setSymbolExpiryFilter] = useState('');
  const [bidMinInput, setBidMinInput] = useState('');
  const [askMaxInput, setAskMaxInput] = useState('');
  const [fromDateTimeInput, setFromDateTimeInput] = useState('');
  const [toDateTimeInput, setToDateTimeInput] = useState('');
  const [excludedSymbolExchanges, setExcludedSymbolExchanges] = useState<string[]>([]);
  const [selectedScrape, setSelectedScrape] = useState<SelectedScrape | null>(null);
  const [filterExchange, setFilterExchange] = useState('all');
  const [anomalySymbol, setAnomalySymbol] = useState('');
  const [anomalyExchange, setAnomalyExchange] = useState('gate');
  const [anomalyBidThresholdInput, setAnomalyBidThresholdInput] = useState('');
  const [anomalyAskThresholdInput, setAnomalyAskThresholdInput] = useState('');
  const [globalBidThresholdInput, setGlobalBidThresholdInput] = useState('500');
  const [globalAskThresholdInput, setGlobalAskThresholdInput] = useState('500');
  const [anomalyExpiryFromInput, setAnomalyExpiryFromInput] = useState('');
  const [anomalyExpiryToInput, setAnomalyExpiryToInput] = useState('');
  const [anomalyTokenFilterInput, setAnomalyTokenFilterInput] = useState('');
  const [anomalyTokenFilter, setAnomalyTokenFilter] = useState('');
  const [anomalyOptionTypeFilter, setAnomalyOptionTypeFilter] = useState<'all' | 'C' | 'P'>('all');
  const [anomalySelectedExchanges, setAnomalySelectedExchanges] = useState<string[]>([]);
  const [savingAnomaly, setSavingAnomaly] = useState(false);
  const [savingGlobal, setSavingGlobal] = useState(false);
  const [savingAnomalyNotifications, setSavingAnomalyNotifications] = useState(false);
  const [savingAnomalyExchangeNotification, setSavingAnomalyExchangeNotification] = useState<string | null>(null);
  const [anomalyOverviewPage, setAnomalyOverviewPage] = useState(0);
  const [anomalyOverviewPageInput, setAnomalyOverviewPageInput] = useState('1');
  const [anomalyOverviewPageInputError, setAnomalyOverviewPageInputError] = useState(false);
  const [pendingAnomalyOverviewScroll, setPendingAnomalyOverviewScroll] = useState(false);
  const anomalyOverviewTopRef = useRef<HTMLDivElement | null>(null);
  const tabScrollTopRef = useRef<Record<ZetaTab, number>>({
    symbol: 0,
    scrapes: 0,
    anomalies: 0,
  });
  const anomalyOverviewPageSize = 40;
  const trimmed = symbol.trim().toUpperCase();
  const trimmedAnomalySymbol = anomalySymbol.trim().toUpperCase();
  const fromDateTimeIso = toIsoFromDateTimeLocal(fromDateTimeInput);
  const toDateTimeIso = toIsoFromDateTimeLocal(toDateTimeInput);
  const bidMin = Number(bidMinInput);
  const askMax = Number(askMaxInput);
  const hasBidMin = bidMinInput.trim() !== '' && Number.isFinite(bidMin);
  const hasAskMax = askMaxInput.trim() !== '' && Number.isFinite(askMax);

  const selectTab = (t: ZetaTab): void => {
    tabScrollTopRef.current[tab] = window.scrollY;
    if (tab === t) return;
    setVisitedTabs((prev) => (prev[t] ? prev : { ...prev, [t]: true }));
    setTab(t);
  };

  const items = useSupabaseQuery<ZetaSnapshot[]>(
    () =>
      trimmed
        ? (() => {
            let q = sb
              .from('zeta_snapshots')
              .select('*')
              .eq('symbol', trimmed)
              .order('taken_at', { ascending: false });
            if (fromDateTimeIso) q = q.gte('taken_at', fromDateTimeIso);
            if (toDateTimeIso) q = q.lte('taken_at', toDateTimeIso);
            return q.limit(500);
          })()
        : Promise.resolve({ data: [], error: null }),
    [trimmed, fromDateTimeIso, toDateTimeIso],
  );

  const symbolExchanges = useMemo(
    () => [...new Set((items.data ?? []).map((s) => s.exchange))].sort((a, b) => exchangeLabel(a).localeCompare(exchangeLabel(b), 'ru')),
    [items.data],
  );

  useEffect(() => {
    setExcludedSymbolExchanges([]);
  }, [trimmed]);

  const visibleSymbolExchanges = useMemo(
    () => symbolExchanges.filter((exchange) => !excludedSymbolExchanges.includes(exchange)),
    [symbolExchanges, excludedSymbolExchanges],
  );

  const groupedRows = useMemo(() => {
    const bucketedRows = new Map<number, Map<string, ZetaSnapshot>>();
    for (const item of items.data ?? []) {
      if (excludedSymbolExchanges.includes(item.exchange)) continue;
      const bucketMs = toBucketMs(item.taken_at);
      const byExchange = bucketedRows.get(bucketMs) ?? new Map<string, ZetaSnapshot>();
      const prev = byExchange.get(item.exchange);
      if (!prev) {
        byExchange.set(item.exchange, item);
      } else {
        const prevTakenAtMs = new Date(prev.taken_at).getTime();
        const itemTakenAtMs = new Date(item.taken_at).getTime();
        const prevDiff = Math.abs(prevTakenAtMs - bucketMs);
        const nextDiff = Math.abs(itemTakenAtMs - bucketMs);
        if (nextDiff < prevDiff || (nextDiff === prevDiff && itemTakenAtMs > prevTakenAtMs)) {
          byExchange.set(item.exchange, item);
        }
      }
      bucketedRows.set(bucketMs, byExchange);
    }
    return [...bucketedRows.entries()]
      .sort(([a], [b]) => b - a)
      .map(([bucketMs, byExchange]) => ({
        bucketMs,
        byExchange,
      }));
  }, [items.data, excludedSymbolExchanges]);

  const filteredGroupedRows = useMemo(
    () =>
      groupedRows.filter((row) => {
        const rowItems = visibleSymbolExchanges
          .map((exchange) => row.byExchange.get(exchange))
          .filter((item): item is ZetaSnapshot => Boolean(item));
        if (!rowItems.length) return false;
        if (!hasBidMin && !hasAskMax) return true;
        return rowItems.some((item) => {
          if (hasBidMin && item.bid < bidMin) return false;
          if (hasAskMax && item.ask > askMax) return false;
          return true;
        });
      }),
    [groupedRows, visibleSymbolExchanges, hasBidMin, bidMin, hasAskMax, askMax],
  );

  // Снимки для графика и CSV: все строки выбранного периода, кроме исключённых бирж.
  const symbolHistoryRows = useMemo(
    () => (items.data ?? []).filter((item) => !excludedSymbolExchanges.includes(item.exchange)),
    [items.data, excludedSymbolExchanges],
  );

  const exportSymbolHistoryCsv = (): void => {
    if (!symbolHistoryRows.length) {
      notify('error');
      return;
    }
    const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
    downloadTextFile(`${trimmed}_${stamp}.csv`, buildSymbolHistoryCsv(symbolHistoryRows), 'text/csv;charset=utf-8');
    notify('success');
  };

  // Автодополнение символов из таблицы active_options (с авто-обновлением при устаревших данных).
  const activeOptions = useEnsureActiveOptionsFresh();

  const symbolExpiryDates = useMemo(() => {
    const now = Date.now();
    const dates = new Set<string>();
    for (const o of activeOptions.data ?? []) {
      if (new Date(o.expiry).getTime() < now) continue;
      const expiry = extractOptionExpiry(o.symbol);
      if (expiry) dates.add(expiry);
    }
    return [...dates].sort();
  }, [activeOptions.data]);

  const filteredSymbolOptions = useMemo(() => {
    const now = Date.now();
    const q = symbolSearchInput.trim().toUpperCase();
    return (activeOptions.data ?? [])
      .filter((o) => new Date(o.expiry).getTime() >= now)
      .map((o) => o.symbol)
      .filter((sym) => {
        if (q && !sym.includes(q)) return false;
        if (symbolSideFilter !== 'all' && extractOptionSide(sym) !== symbolSideFilter) return false;
        if (symbolExpiryFilter && extractOptionExpiry(sym) !== symbolExpiryFilter) return false;
        return true;
      })
      .sort();
  }, [activeOptions.data, symbolSearchInput, symbolSideFilter, symbolExpiryFilter]);

  const overThirtyDayOptions = useMemo(() => {
    return (activeOptions.data ?? [])
      .filter((o) => {
        const d = daysUntil(o.expiry);
        return d > 30;
      })
      .map((o) => o.symbol)
      .sort();
  }, [activeOptions.data]);

  const overThirtyDayExpiryMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const o of activeOptions.data ?? []) {
      const d = daysUntil(o.expiry);
      if (d <= 30) continue;
      map.set(o.symbol, Math.max(31, Math.ceil(d)));
    }
    return map;
  }, [activeOptions.data]);

  const anomalyWatchers = useSupabaseQuery<ZetaAnomalyWatcher[]>(
    () =>
      userId
        ? sb
            .from('zeta_anomaly_watchers')
            .select('*')
            .eq('user_id', userId)
            .eq('is_active', true)
            .or('bid_threshold_percent.not.is.null,ask_threshold_percent.not.is.null')
            .order('created_at', { ascending: false })
        : Promise.resolve({ data: [], error: null }),
    [userId],
    30_000,
  );

  const anomalyGlobalSettings = useSupabaseQuery<ZetaAnomalySettings[]>(
    () =>
      userId
        ? sb
            .from('zeta_anomaly_user_settings')
            .select('*')
            .eq('user_id', userId)
            .limit(1)
        : Promise.resolve({ data: [], error: null }),
    [userId],
    30_000,
  );

  const anomalyExchangeNotificationSettings = useSupabaseQuery<ZetaExchangeNotificationSetting[]>(
    () =>
      userId
        ? sb
            .from('zeta_anomaly_exchange_notification_settings')
            .select('user_id, exchange, enabled')
            .eq('user_id', userId)
        : Promise.resolve({ data: [], error: null }),
    [userId],
    30_000,
  );

  const anomalyStats = useSupabaseQuery<ZetaTrimmedStats[]>(
    () =>
      trimmedAnomalySymbol && anomalyExchange
        ? sb.rpc('get_zeta_option_trimmed_stats', {
            p_option_id: trimmedAnomalySymbol,
            p_exchange: anomalyExchange,
          })
        : Promise.resolve({ data: [], error: null }),
    [trimmedAnomalySymbol, anomalyExchange],
  );

  const anomalyExchanges = useSupabaseQuery<Array<{ exchange: string | null }>>(
    () =>
      visitedTabs.anomalies
        ? sb.rpc('get_zeta_anomaly_exchanges')
        : Promise.resolve({ data: [], error: null }),
    [visitedTabs.anomalies],
    60_000,
  );

  const activeAnomalyExchanges = useMemo(() => {
    const set = new Set<string>();
    for (const row of anomalyExchanges.data ?? []) {
      const exchange = row.exchange?.trim().toLowerCase();
      if (exchange) set.add(exchange);
    }
    for (const watcher of anomalyWatchers.data ?? []) {
      const exchange = watcher.exchange?.trim().toLowerCase();
      if (exchange) set.add(exchange);
    }
    return [...set].sort((a, b) => exchangeLabel(a).localeCompare(exchangeLabel(b), 'ru'));
  }, [anomalyExchanges.data, anomalyWatchers.data]);

  // Фильтр бирж для RPC: null, если не выбрано ни одной или выбраны все активные биржи
  // (тогда сервер не фильтрует). Ключ-строка — стабильная зависимость для useSupabaseQuery.
  const anomalyExchangesFilterKey = useMemo(() => {
    const selected = [...new Set(anomalySelectedExchanges.map((e) => e.trim().toLowerCase()).filter(Boolean))].sort();
    if (!selected.length) return '';
    if (activeAnomalyExchanges.length && activeAnomalyExchanges.every((e) => selected.includes(e))) return '';
    return selected.join(',');
  }, [anomalySelectedExchanges, activeAnomalyExchanges]);
  const anomalyExchangesParam = anomalyExchangesFilterKey ? anomalyExchangesFilterKey.split(',') : null;

  const anomalyOverview = useSupabaseQuery<ZetaAnomalyOverviewRow[]>(
    () =>
      visitedTabs.anomalies
        ? sb.rpc('get_zeta_anomaly_options_overview', {
            p_limit: anomalyOverviewPageSize,
            p_offset: anomalyOverviewPage * anomalyOverviewPageSize,
            p_expiry_from: anomalyExpiryFromInput || null,
            p_expiry_to: anomalyExpiryToInput || null,
            p_base_query: anomalyTokenFilter || null,
            p_option_side: anomalyOptionTypeFilter === 'all' ? null : anomalyOptionTypeFilter,
            p_exchanges: anomalyExchangesParam,
          })
        : Promise.resolve({ data: [], error: null }),
    [visitedTabs.anomalies, anomalyOverviewPage, anomalyExpiryFromInput, anomalyExpiryToInput, anomalyTokenFilter, anomalyOptionTypeFilter, anomalyExchangesFilterKey],
    30_000,
  );

  const anomalyOverviewCount = useSupabaseQuery<number>(
    () =>
      visitedTabs.anomalies
        ? sb.rpc('get_zeta_anomaly_options_overview_count', {
            p_expiry_from: anomalyExpiryFromInput || null,
            p_expiry_to: anomalyExpiryToInput || null,
            p_base_query: anomalyTokenFilter || null,
            p_option_side: anomalyOptionTypeFilter === 'all' ? null : anomalyOptionTypeFilter,
            p_exchanges: anomalyExchangesParam,
          }).then((res) => ({
            data: Number(res.data ?? 0),
            error: res.error,
          }))
        : Promise.resolve({ data: 0, error: null }),
    [visitedTabs.anomalies, anomalyExpiryFromInput, anomalyExpiryToInput, anomalyTokenFilter, anomalyOptionTypeFilter, anomalyExchangesFilterKey],
    30_000,
  );

  // Зависим от примитивов, а не от массива: поллинг каждые 30 сек создаёт новый
  // массив и иначе перезатирал бы ввод пользователя.
  const hasGlobalSettings = Boolean(anomalyGlobalSettings.data?.[0]);
  const savedGlobalBidThreshold = anomalyGlobalSettings.data?.[0]?.global_bid_threshold_percent ?? 500;
  const savedGlobalAskThreshold = anomalyGlobalSettings.data?.[0]?.global_ask_threshold_percent ?? 500;
  useEffect(() => {
    if (!hasGlobalSettings) return;
    setGlobalBidThresholdInput(String(savedGlobalBidThreshold));
    setGlobalAskThresholdInput(String(savedGlobalAskThreshold));
  }, [hasGlobalSettings, savedGlobalBidThreshold, savedGlobalAskThreshold]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setAnomalyTokenFilter(anomalyTokenFilterInput.trim().toUpperCase());
    }, ZETA_FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [anomalyTokenFilterInput]);

  useEffect(() => {
    if (!activeAnomalyExchanges.length) return;
    if (activeAnomalyExchanges.includes(anomalyExchange)) return;
    setAnomalyExchange(activeAnomalyExchanges[0]);
  }, [activeAnomalyExchanges, anomalyExchange]);

  useEffect(() => {
    if (!activeAnomalyExchanges.length) {
      setAnomalySelectedExchanges([]);
      return;
    }
    setAnomalySelectedExchanges((prev) => {
      if (!prev.length) return activeAnomalyExchanges;
      const next = prev.filter((exchange) => activeAnomalyExchanges.includes(exchange));
      return next.length ? next : activeAnomalyExchanges;
    });
  }, [activeAnomalyExchanges]);

  const anomalyNotificationsEnabled = anomalyGlobalSettings.data?.[0]?.anomaly_notifications_enabled !== false;

  const anomalyExchangeNotificationMap = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const exchange of activeAnomalyExchanges) map.set(exchange, true);
    for (const row of anomalyExchangeNotificationSettings.data ?? []) {
      const exchange = row.exchange?.trim().toLowerCase();
      if (!exchange) continue;
      map.set(exchange, row.enabled !== false);
    }
    return map;
  }, [activeAnomalyExchanges, anomalyExchangeNotificationSettings.data]);

  const currentAnomalyWatcher = useMemo(
    () =>
      (anomalyWatchers.data ?? []).find(
        (w) => w.option_id === trimmedAnomalySymbol && w.exchange === anomalyExchange,
      ) ?? null,
    [anomalyWatchers.data, trimmedAnomalySymbol, anomalyExchange],
  );

  // Аналогично: пересеиваем ввод только при смене watcher'а или сохранённых порогов.
  const currentWatcherId = currentAnomalyWatcher?.id ?? null;
  const currentWatcherBid = currentAnomalyWatcher?.bid_threshold_percent ?? null;
  const currentWatcherAsk = currentAnomalyWatcher?.ask_threshold_percent ?? null;
  useEffect(() => {
    setAnomalyBidThresholdInput(currentWatcherBid != null ? String(currentWatcherBid) : '');
    setAnomalyAskThresholdInput(currentWatcherAsk != null ? String(currentWatcherAsk) : '');
  }, [currentWatcherId, currentWatcherBid, currentWatcherAsk]);

  // Список скрапов (уникальные временные метки с числом символов, по биржам).
  const scrapes = useSupabaseQuery<ScrapeTs[]>(
    () =>
      visitedTabs.scrapes
        ? sb.rpc('get_zeta_scrape_timestamps', { p_limit: 200 })
        : Promise.resolve({ data: [], error: null }),
    [visitedTabs.scrapes],
    60_000,
  );

  // Котировки для выбранного скрапа. Используем JSON-RPC (get_zeta_scrape_quotes_json),
  // которая возвращает одну jsonb-строку с массивом всех котировок — обход глобального
  // db-max-rows = 1000 PostgREST (setof-RPC режется на 1000, jsonb-строка — нет).
  const scrapeQuotes = useSupabaseQuery<ScrapeQuote[]>(
    () =>
      selectedScrape
        ? sb
            .rpc('get_zeta_scrape_quotes_json', {
              p_taken_at: selectedScrape.taken_at,
              p_exchange: selectedScrape.exchange,
            })
            .then((res) => ({
              // PostgREST для scalar-RPC, возвращающей jsonb, отдаёт сам массив (supabase-js
              // разворачивает scalar-ответы автоматически — НЕ заворачивает в [{ fn_name: ... }]).
              data: (res.data as ScrapeQuote[] | null) ?? [],
              error: res.error,
            }))
        : Promise.resolve({ data: [], error: null }),
    [selectedScrape?.taken_at, selectedScrape?.exchange],
  );

  // Spot-цены базовых активов для выбранного скрапа (Gate index_price на момент скрапа).
  const scrapeUnderlyings = useSupabaseQuery<ScrapeUnderlying[]>(
    () =>
      selectedScrape
        ? sb.rpc('get_zeta_scrape_underlyings', {
            p_taken_at: selectedScrape.taken_at,
            p_source: 'gate',
          })
        : Promise.resolve({ data: [], error: null }),
    [selectedScrape?.taken_at],
  );

  // Уникальные биржи из загруженных скрапов (для фильтра).
  const availableExchanges = useMemo(
    () => [...new Set((scrapes.data ?? []).map((s) => s.exchange))].sort(),
    [scrapes.data],
  );

  // Список скрапов после фильтрации по бирже.
  const filteredScrapes = useMemo(
    () =>
      filterExchange === 'all'
        ? (scrapes.data ?? [])
        : (scrapes.data ?? []).filter((s) => s.exchange === filterExchange),
    [scrapes.data, filterExchange],
  );

  const trimmedStatsRow = anomalyStats.data?.[0] ?? null;
  const totalTrackedOptions = Math.max(0, Number(anomalyOverviewCount.data ?? 0));
  const totalOverviewPages = Math.max(1, Math.ceil(totalTrackedOptions / anomalyOverviewPageSize));

  useEffect(() => {
    if (anomalyOverviewCount.isLoading || anomalyOverviewCount.data === null) return;
    if (anomalyOverviewPage < totalOverviewPages) return;
    setAnomalyOverviewPage(Math.max(0, totalOverviewPages - 1));
  }, [anomalyOverviewCount.data, anomalyOverviewCount.isLoading, anomalyOverviewPage, totalOverviewPages]);

  useEffect(() => {
    setAnomalyOverviewPage(0);
  }, [anomalyExpiryFromInput, anomalyExpiryToInput, anomalyTokenFilter, anomalyOptionTypeFilter, anomalyExchangesFilterKey]);

  useEffect(() => {
    setAnomalyOverviewPageInput(String(anomalyOverviewPage + 1));
    setAnomalyOverviewPageInputError(false);
  }, [anomalyOverviewPage]);

  const anomalyOverviewByOption = useMemo(() => {
    const grouped = new Map<string, { expiry_days: number; rows: ZetaAnomalyOverviewRow[] }>();
    for (const row of anomalyOverview.data ?? []) {
      const current = grouped.get(row.option_id);
      if (current) {
        current.rows.push(row);
        continue;
      }
      grouped.set(row.option_id, {
        expiry_days: row.expiry_days,
        rows: [row],
      });
    }
    return [...grouped.entries()].map(([optionId, entry]) => ({
      optionId,
      expiryDays: entry.expiry_days,
      rows: entry.rows,
    }));
  }, [anomalyOverview.data]);

  const saveGlobalThresholds = async (): Promise<void> => {
    if (!userId) return;
    const bid = Number(globalBidThresholdInput.replace(/,/g, '.'));
    const ask = Number(globalAskThresholdInput.replace(/,/g, '.'));
    if (!Number.isFinite(bid) || bid <= 0 || !Number.isFinite(ask) || ask <= 0) {
      notify('error');
      return;
    }
    setSavingGlobal(true);
    const { error } = await sb.from('zeta_anomaly_user_settings').upsert({
      user_id: userId,
      global_bid_threshold_percent: bid,
      global_ask_threshold_percent: ask,
      updated_at: new Date().toISOString(),
    });
    setSavingGlobal(false);
    if (error) {
      notify('error');
      return;
    }
    notify('success');
    anomalyGlobalSettings.refetch();
  };

  const toggleAnomalyNotifications = async (enabled: boolean): Promise<void> => {
    if (!userId) return;
    setSavingAnomalyNotifications(true);
    const { error } = await sb.from('zeta_anomaly_user_settings').upsert({
      user_id: userId,
      anomaly_notifications_enabled: enabled,
      updated_at: new Date().toISOString(),
    });
    setSavingAnomalyNotifications(false);
    if (error) {
      notify('error');
      return;
    }
    notify('success');
    anomalyGlobalSettings.refetch();
  };

  const toggleAnomalyExchangeNotification = async (exchange: string, enabled: boolean): Promise<void> => {
    if (!userId) return;
    setSavingAnomalyExchangeNotification(exchange);
    const { error } = await sb.from('zeta_anomaly_exchange_notification_settings').upsert({
      user_id: userId,
      exchange,
      enabled,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'user_id,exchange' });
    setSavingAnomalyExchangeNotification(null);
    if (error) {
      notify('error');
      return;
    }
    notify('success');
    anomalyExchangeNotificationSettings.refetch();
  };

  const goToAnomalyOverviewPage = (): void => {
    const raw = anomalyOverviewPageInput.trim();
    if (!/^\d+$/.test(raw)) {
      setAnomalyOverviewPageInputError(true);
      return;
    }
    const nextPage = Math.min(totalOverviewPages, Math.max(1, Number(raw)));
    if (nextPage - 1 === anomalyOverviewPage) return;
    setPendingAnomalyOverviewScroll(true);
    setAnomalyOverviewPage(nextPage - 1);
  };

  useEffect(() => {
    if (!pendingAnomalyOverviewScroll) return;
    let cancelled = false;
    const frame = requestAnimationFrame(() => {
      if (cancelled) return;
      anomalyOverviewTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setPendingAnomalyOverviewScroll(false);
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, [pendingAnomalyOverviewScroll]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      window.scrollTo({ top: tabScrollTopRef.current[tab] ?? 0, behavior: 'auto' });
    });
    return () => cancelAnimationFrame(frame);
  }, [tab]);

  const saveAnomalyWatcher = async (): Promise<void> => {
    if (!userId || !trimmedAnomalySymbol || !anomalyExchange) {
      notify('error');
      return;
    }
    if (!overThirtyDayOptions.includes(trimmedAnomalySymbol)) {
      notify('error');
      return;
    }
    const rawBid = anomalyBidThresholdInput.trim().replace(/,/g, '.');
    const rawAsk = anomalyAskThresholdInput.trim().replace(/,/g, '.');
    const bid = rawBid ? Number(rawBid) : null;
    const ask = rawAsk ? Number(rawAsk) : null;
    if ((bid != null && (!Number.isFinite(bid) || bid <= 0)) || (ask != null && (!Number.isFinite(ask) || ask <= 0))) {
      notify('error');
      return;
    }
    if (bid == null && ask == null) {
      notify('error');
      return;
    }

    setSavingAnomaly(true);
    const { error } = await sb.from('zeta_anomaly_watchers').upsert({
      user_id: userId,
      option_id: trimmedAnomalySymbol,
      exchange: anomalyExchange,
      expiry_days: overThirtyDayExpiryMap.get(trimmedAnomalySymbol) ?? 31,
      bid_threshold_percent: bid,
      ask_threshold_percent: ask,
      is_active: true,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'user_id,option_id,exchange' });
    setSavingAnomaly(false);
    if (error) {
      notify('error');
      return;
    }
    notify('success');
    anomalyWatchers.refetch();
  };

  const removeAnomalyWatcher = async (id: number): Promise<void> => {
    const { error } = await sb.from('zeta_anomaly_watchers').update({ is_active: false }).eq('id', id);
    if (error) {
      notify('error');
      return;
    }
    notify('success');
    anomalyWatchers.refetch();
  };

  return (
    <Page title="Zeta — история">
      {/* Табы: По символу / История скрапов / Аномалии */}
      <div
        style={{
          display: 'flex',
          margin: '0 var(--space-md) var(--space-md)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          border: '0.5px solid var(--tg-separator)',
        }}
      >
        {(['symbol', 'scrapes', 'anomalies'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => selectTab(t)}
            style={{
              flex: 1,
              minHeight: 40,
              border: 'none',
              background: tab === t ? 'var(--tg-button)' : 'transparent',
              color: tab === t ? 'var(--tg-button-text)' : 'var(--tg-text)',
              fontSize: 'var(--font-md)',
              cursor: 'pointer',
              fontWeight: tab === t ? 600 : 400,
            }}
          >
            {t === 'symbol' ? 'По символу' : t === 'scrapes' ? 'История скрапов' : 'Аномалии Bid/Ask'}
          </button>
        ))}
      </div>

      {tab === 'symbol' ? (
        <>
          <Section header="Опцион" footer="Снимки сохраняются каждые 30 минут по всем активным символам.">
            <div style={{ padding: '0 var(--space-lg) var(--space-sm)', display: 'grid', gap: 'var(--space-sm)' }}>
              <input
                value={symbolSearchInput}
                onChange={(e) => setSymbolSearchInput(e.target.value)}
                placeholder="Поиск по тикеру опциона…"
                autoCapitalize="characters"
                style={{
                  width: '100%',
                  background: 'var(--tg-secondary-bg)',
                  border: 'none',
                  outline: 'none',
                  padding: 'var(--space-sm) var(--space-md)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 'var(--font-md)',
                  color: 'var(--tg-text)',
                  boxSizing: 'border-box',
                }}
              />
              <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
                {(['all', 'C', 'P'] as const).map((side) => (
                  <button
                    key={side}
                    type="button"
                    onClick={() => setSymbolSideFilter(side)}
                    style={{
                      padding: '4px 12px',
                      borderRadius: 'var(--radius-sm)',
                      border: '0.5px solid var(--tg-separator)',
                      background: symbolSideFilter === side ? 'var(--tg-button)' : 'var(--tg-secondary-bg)',
                      color: symbolSideFilter === side ? 'var(--tg-button-text)' : 'var(--tg-text)',
                      fontSize: 'var(--font-sm)',
                      cursor: 'pointer',
                      fontWeight: symbolSideFilter === side ? 600 : 400,
                    }}
                  >
                    {side === 'all' ? 'Все' : side === 'C' ? 'Call' : 'Put'}
                  </button>
                ))}
                <select
                  value={symbolExpiryFilter}
                  onChange={(e) => setSymbolExpiryFilter(e.target.value)}
                  style={{
                    flex: 1,
                    minWidth: 140,
                    background: symbolExpiryFilter ? 'var(--tg-button)' : 'var(--tg-secondary-bg)',
                    color: symbolExpiryFilter ? 'var(--tg-button-text)' : 'var(--tg-text)',
                    border: '0.5px solid var(--tg-separator)',
                    borderRadius: 'var(--radius-sm)',
                    padding: '4px var(--space-sm)',
                    fontSize: 'var(--font-sm)',
                    cursor: 'pointer',
                  }}
                >
                  <option value="">Все даты</option>
                  {symbolExpiryDates.map((d) => (
                    <option key={d} value={d}>{fmtOptionExpiry(d)}</option>
                  ))}
                </select>
              </div>
            </div>
            <SymbolPicker
              label="Символ"
              value={symbol}
              onChange={setSymbol}
              options={filteredSymbolOptions}
              placeholder="BTC-20250131-50000-C"
              isLoading={activeOptions.isLoading}
            />
          </Section>

          <Section header="История">
            {!trimmed ? (
              <Empty icon="🔎" title="Введите символ" hint="Например, BTC-20250131-50000-C" />
            ) : (
              <>
                {/* Фильтры показываем всегда, даже при пустом результате — иначе нельзя сбросить фильтр, давший 0 строк. */}
                <div style={{ padding: 'var(--space-md) var(--space-lg)', display: 'grid', gap: 'var(--space-sm)' }}>
                  <input
                    value={bidMinInput}
                    onChange={(e) => setBidMinInput(e.target.value.replace(/,/g, '.'))}
                    inputMode="decimal"
                    placeholder="Bid ≥"
                    style={{
                      width: '100%',
                      background: 'var(--tg-secondary-bg)',
                      color: 'var(--tg-text)',
                      border: 'none',
                      borderRadius: 'var(--radius-sm)',
                      padding: 'var(--space-xs) var(--space-sm)',
                      minHeight: 36,
                    }}
                  />
                  <input
                    value={askMaxInput}
                    onChange={(e) => setAskMaxInput(e.target.value.replace(/,/g, '.'))}
                    inputMode="decimal"
                    placeholder="Ask ≤"
                    style={{
                      width: '100%',
                      background: 'var(--tg-secondary-bg)',
                      color: 'var(--tg-text)',
                      border: 'none',
                      borderRadius: 'var(--radius-sm)',
                      padding: 'var(--space-xs) var(--space-sm)',
                      minHeight: 36,
                    }}
                  />
                  <input
                    type="datetime-local"
                    value={fromDateTimeInput}
                    onChange={(e) => setFromDateTimeInput(e.target.value)}
                    placeholder="От"
                    style={{
                      width: '100%',
                      background: 'var(--tg-secondary-bg)',
                      color: 'var(--tg-text)',
                      border: 'none',
                      borderRadius: 'var(--radius-sm)',
                      padding: 'var(--space-xs) var(--space-sm)',
                      minHeight: 36,
                    }}
                  />
                  <input
                    type="datetime-local"
                    value={toDateTimeInput}
                    onChange={(e) => setToDateTimeInput(e.target.value)}
                    placeholder="До"
                    style={{
                      width: '100%',
                      background: 'var(--tg-secondary-bg)',
                      color: 'var(--tg-text)',
                      border: 'none',
                      borderRadius: 'var(--radius-sm)',
                      padding: 'var(--space-xs) var(--space-sm)',
                      minHeight: 36,
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setBidMinInput('');
                      setAskMaxInput('');
                      setFromDateTimeInput('');
                      setToDateTimeInput('');
                      setExcludedSymbolExchanges([]);
                    }}
                    style={{
                      width: '100%',
                      minHeight: 36,
                      border: 'none',
                      borderRadius: 'var(--radius-sm)',
                      background: 'var(--tg-secondary-bg)',
                      color: 'var(--tg-text)',
                      cursor: 'pointer',
                    }}
                  >
                    Сбросить
                  </button>
                  {!!symbolExchanges.length && (
                    <div style={{ display: 'grid', gap: 'var(--space-xs)' }}>
                      <span style={{ color: 'var(--tg-hint)', fontSize: 12 }}>Исключить биржи:</span>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-xs)' }}>
                        {symbolExchanges.map((exchange) => {
                          const excluded = excludedSymbolExchanges.includes(exchange);
                          return (
                            <button
                              key={exchange}
                              type="button"
                              onClick={() =>
                                setExcludedSymbolExchanges((prev) =>
                                  prev.includes(exchange) ? prev.filter((x) => x !== exchange) : [...prev, exchange],
                                )
                              }
                              style={{
                                border: 'none',
                                borderRadius: '999px',
                                background: excluded ? 'var(--tg-secondary-bg)' : 'var(--tg-button)',
                                color: excluded ? 'var(--tg-text)' : 'var(--tg-button-text)',
                                minHeight: 30,
                                padding: '0 var(--space-sm)',
                                cursor: 'pointer',
                                fontSize: '12px',
                              }}
                            >
                              {excluded ? `✕ ${exchangeLabel(exchange)}` : exchangeLabel(exchange)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                {items.isLoading ? (
                  <Spinner />
                ) : !items.data?.length ? (
                  fromDateTimeIso || toDateTimeIso ? (
                    <Empty icon="🔍" title="Ничего не найдено" hint="За выбранный период снимков нет — измените даты или нажмите «Сбросить»." />
                  ) : (
                    <Empty icon="📊" title="Нет данных" hint="Снимки собираются каждые 30 минут — первый появится в течение получаса после начала торгов символом." />
                  )
                ) : !filteredGroupedRows.length || !visibleSymbolExchanges.length ? (
                  <Empty icon="🔍" title="Ничего не найдено" hint="Попробуйте изменить фильтры Bid/Ask и исключения бирж." />
                ) : (
                  <>
                  <ZetaHistoryChart rows={symbolHistoryRows} exchanges={visibleSymbolExchanges} exchangeLabel={exchangeLabel} />
                  <div style={{ padding: '0 var(--space-lg) var(--space-md)' }}>
                    <button
                      type="button"
                      onClick={exportSymbolHistoryCsv}
                      style={{
                        width: '100%',
                        minHeight: 36,
                        border: '0.5px solid var(--tg-separator)',
                        borderRadius: 'var(--radius-sm)',
                        background: 'var(--tg-secondary-bg)',
                        color: 'var(--tg-text)',
                        cursor: 'pointer',
                      }}
                    >
                      {`Экспорт CSV (${symbolHistoryRows.length})`}
                    </button>
                  </div>
                  <div style={{ overflowX: 'auto', padding: '0 var(--space-md) var(--space-md)' }}>
                    <table style={{ width: '100%', minWidth: '360px', borderCollapse: 'separate', borderSpacing: 0, fontSize: '12px' }}>
                      <thead>
                        <tr>
                          <th
                            style={{
                              position: 'sticky',
                              left: 0,
                              zIndex: 2,
                              background: 'var(--tg-section-bg)',
                              textAlign: 'left',
                              padding: 'var(--space-sm)',
                              borderBottom: '0.5px solid var(--tg-separator)',
                              borderRight: '0.5px solid var(--tg-separator)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Время
                          </th>
                          {visibleSymbolExchanges.map((exchange) => (
                            <th
                              key={exchange}
                              style={{
                                textAlign: 'left',
                                padding: 'var(--space-sm)',
                                borderBottom: '0.5px solid var(--tg-separator)',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {exchangeLabel(exchange)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredGroupedRows.map((row) => (
                          <tr key={row.bucketMs}>
                            <td
                              style={{
                                position: 'sticky',
                                left: 0,
                                zIndex: 1,
                                background: 'var(--tg-section-bg)',
                                padding: 'var(--space-sm)',
                                borderBottom: '0.5px solid var(--tg-separator)',
                                borderRight: '0.5px solid var(--tg-separator)',
                                whiteSpace: 'nowrap',
                                verticalAlign: 'top',
                              }}
                            >
                              {fmtDateTime(new Date(row.bucketMs).toISOString())}
                            </td>
                            {visibleSymbolExchanges.map((exchange) => {
                              const item = row.byExchange.get(exchange);
                              return (
                                <td
                                  key={`${row.bucketMs}-${exchange}`}
                                  style={{
                                    padding: 'var(--space-sm)',
                                    borderBottom: '0.5px solid var(--tg-separator)',
                                    whiteSpace: 'nowrap',
                                    verticalAlign: 'top',
                                  }}
                                >
                                  {item ? `${item.bid} / ${item.ask}` : '—'}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  </>
                )}
              </>
            )}
          </Section>
        </>
      ) : tab === 'scrapes' ? (
        <>
          {selectedScrape ? (
            <>
              {/* Заголовок выбранного скрапа */}
              <Section
                header={`${exchangeLabel(selectedScrape.exchange)} · ${fmtDateTime(selectedScrape.taken_at)}`}
                footer="Котировки опционов на выбранный момент времени."
              >
                <button
                  type="button"
                  onClick={() => setSelectedScrape(null)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-sm)',
                    padding: 'var(--space-md) var(--space-lg)',
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--tg-button)',
                    fontSize: 'var(--font-md)',
                    cursor: 'pointer',
                    minHeight: 'var(--tap-target)',
                    borderBottom: '0.5px solid var(--tg-separator)',
                    width: '100%',
                    textAlign: 'left',
                  }}
                >
                  ← К списку скрапов
                </button>

                {/* Таблица котировок с фильтрами (аналогично Epsilon QuickAdd) */}
                <ScrapeQuotesTable
                  quotes={scrapeQuotes.data ?? []}
                  isLoading={scrapeQuotes.isLoading}
                  underlyingPrices={scrapeUnderlyings.data ?? []}
                />
              </Section>
            </>
          ) : (
            <>
              {/* Фильтр по бирже */}
              {!scrapes.isLoading && (scrapes.data?.length ?? 0) > 0 && (
                <div style={{ padding: '0 var(--space-md) var(--space-md)' }}>
                  <select
                    value={filterExchange}
                    onChange={(e) => setFilterExchange(e.target.value)}
                    style={{
                      width: '100%',
                      background: 'var(--tg-section-bg)',
                      border: '0.5px solid var(--tg-separator)',
                      borderRadius: 'var(--radius-md)',
                      padding: 'var(--space-sm) var(--space-md)',
                      fontSize: 'var(--font-md)',
                      color: 'var(--tg-text)',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="all">Все биржи</option>
                    {availableExchanges.map((ex) => (
                      <option key={ex} value={ex}>{exchangeLabel(ex)}</option>
                    ))}
                  </select>
                </div>
              )}
              <Section header="Скрапы" footer="Каждые 30 минут сохраняется снимок котировок по всем активным опционам и биржам.">
                {scrapes.isLoading ? (
                  <Spinner />
                ) : !scrapes.data?.length ? (
                  <Empty icon="📡" title="Нет скрапов" hint="Скрапы появятся после первого запуска zeta-snapshotter." />
                ) : !filteredScrapes.length ? (
                  <Empty icon="🔍" title="Нет скрапов" hint="По выбранной бирже скрапов не найдено." />
                ) : (
                  filteredScrapes.map((s) => (
                    <Cell
                      key={`${s.taken_at}-${s.exchange}`}
                      before={
                        <span style={{ fontSize: 12, color: 'var(--tg-hint)', fontWeight: 600 }}>
                          {exchangeLabel(s.exchange)}
                        </span>
                      }
                      title={fmtDateTime(s.taken_at)}
                      subtitle={`${s.symbol_count} символов`}
                      after={
                        <button
                          type="button"
                          onClick={() => setSelectedScrape({ taken_at: s.taken_at, exchange: s.exchange })}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'var(--tg-button)',
                            fontSize: 'var(--font-md)',
                            cursor: 'pointer',
                            padding: 'var(--space-sm)',
                            minHeight: 'var(--tap-target)',
                          }}
                        >
                          →
                        </button>
                      }
                    />
                  ))
                )}
              </Section>
            </>
          )}
        </>
      ) : (
        <>
          <Section header="Уведомления об аномалиях">
            <div style={{ padding: 'var(--space-md) var(--space-lg)', display: 'grid', gap: 'var(--space-sm)' }}>
              <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-sm)' }}>
                <span style={{ color: 'var(--tg-text)' }}>
                  {`Уведомления об аномалиях: ${anomalyNotificationsEnabled ? 'Вкл' : 'Выкл'}`}
                </span>
                <input
                  type="checkbox"
                  checked={anomalyNotificationsEnabled}
                  disabled={savingAnomalyNotifications || !userId}
                  onChange={(e) => void toggleAnomalyNotifications(e.target.checked)}
                />
              </label>
            </div>
          </Section>

          <Section header="Уведомления по биржам">
            <div style={{ padding: 'var(--space-md) var(--space-lg)', display: 'grid', gap: 'var(--space-xs)' }}>
              {!activeAnomalyExchanges.length ? (
                <div style={{ color: 'var(--tg-hint)', fontSize: 'var(--font-sm)' }}>Нет доступных бирж.</div>
              ) : activeAnomalyExchanges.map((exchange) => (
                <label
                  key={`notif-${exchange}`}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-sm)' }}
                >
                  <span style={{ color: 'var(--tg-text)' }}>
                    {`${exchangeLabel(exchange)}: ${anomalyExchangeNotificationMap.get(exchange) === false ? 'Выкл' : 'Вкл'}`}
                  </span>
                  <input
                    type="checkbox"
                    checked={anomalyExchangeNotificationMap.get(exchange) !== false}
                    disabled={savingAnomalyExchangeNotification === exchange || !userId}
                    onChange={(e) => void toggleAnomalyExchangeNotification(exchange, e.target.checked)}
                  />
                </label>
              ))}
            </div>
          </Section>

          <Section header="Глобальные пороги" footer="По умолчанию 500% для Ask (падение) и Bid (рост).">
            <div style={{ padding: 'var(--space-md) var(--space-lg)', display: 'grid', gap: 'var(--space-sm)' }}>
              <input
                value={globalBidThresholdInput}
                onChange={(e) => setGlobalBidThresholdInput(e.target.value.replace(/,/g, '.'))}
                inputMode="decimal"
                placeholder="Bid рост, %"
                style={{
                  width: '100%',
                  background: 'var(--tg-secondary-bg)',
                  color: 'var(--tg-text)',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  padding: 'var(--space-xs) var(--space-sm)',
                  minHeight: 36,
                }}
              />
              <input
                value={globalAskThresholdInput}
                onChange={(e) => setGlobalAskThresholdInput(e.target.value.replace(/,/g, '.'))}
                inputMode="decimal"
                placeholder="Ask падение, %"
                style={{
                  width: '100%',
                  background: 'var(--tg-secondary-bg)',
                  color: 'var(--tg-text)',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  padding: 'var(--space-xs) var(--space-sm)',
                  minHeight: 36,
                }}
              />
              <Button fullWidth onClick={() => void saveGlobalThresholds()} disabled={savingGlobal || !userId}>
                {savingGlobal ? 'Сохраняю…' : 'Сохранить глобальные пороги'}
              </Button>
            </div>
          </Section>

          <Section
            header="ИНДИВИДУАЛЬНЫЕ ПОРОГИ (опционально)"
            footer="По умолчанию аномалии отслеживаются по всем опционам с экспирацией > 30 дней с использованием глобальных порогов. Здесь можно задать индивидуальный порог только для нужного опциона/биржи."
          >
            <div style={{ padding: '0 var(--space-lg) var(--space-sm)', display: 'grid', gap: 'var(--space-sm)' }}>
              <SymbolPicker
                label="Опцион (экспирация > 30 дней)"
                value={anomalySymbol}
                onChange={setAnomalySymbol}
                options={overThirtyDayOptions}
                placeholder="BTC-20250131-50000-C"
                isLoading={activeOptions.isLoading}
              />
              <select
                value={anomalyExchange}
                onChange={(e) => setAnomalyExchange(e.target.value)}
                disabled={!activeAnomalyExchanges.length}
                style={{
                  width: '100%',
                  background: 'var(--tg-secondary-bg)',
                  color: 'var(--tg-text)',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  padding: 'var(--space-xs) var(--space-sm)',
                  minHeight: 36,
                }}
              >
                {!activeAnomalyExchanges.length ? (
                  <option value="">Нет бирж</option>
                ) : activeAnomalyExchanges.map((exchange) => (
                  <option key={exchange} value={exchange}>{exchangeLabel(exchange)}</option>
                ))}
              </select>
              <input
                value={anomalyBidThresholdInput}
                onChange={(e) => setAnomalyBidThresholdInput(e.target.value.replace(/,/g, '.'))}
                inputMode="decimal"
                placeholder="Индивидуальный Bid рост, % (опционально)"
                style={{
                  width: '100%',
                  background: 'var(--tg-secondary-bg)',
                  color: 'var(--tg-text)',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  padding: 'var(--space-xs) var(--space-sm)',
                  minHeight: 36,
                }}
              />
              <input
                value={anomalyAskThresholdInput}
                onChange={(e) => setAnomalyAskThresholdInput(e.target.value.replace(/,/g, '.'))}
                inputMode="decimal"
                placeholder="Индивидуальный Ask падение, % (опционально)"
                style={{
                  width: '100%',
                  background: 'var(--tg-secondary-bg)',
                  color: 'var(--tg-text)',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  padding: 'var(--space-xs) var(--space-sm)',
                  minHeight: 36,
                }}
              />
              <Button fullWidth onClick={() => void saveAnomalyWatcher()} disabled={savingAnomaly || !userId}>
                {savingAnomaly ? 'Сохраняю…' : currentAnomalyWatcher ? 'Обновить индивидуальный порог' : 'Сохранить индивидуальный порог'}
              </Button>
            </div>

            {trimmedStatsRow && (
              <div style={{ padding: '0 var(--space-lg) var(--space-md)', color: 'var(--tg-hint)', fontSize: 'var(--font-sm)' }}>
                {`Выборка (только > 0): Bid ${trimmedStatsRow.sample_size_bid ?? 0} · Ask ${trimmedStatsRow.sample_size_ask ?? 0} · средний bid: ${fmtPrice(trimmedStatsRow.avg_bid_trimmed)} · средний ask: ${fmtPrice(trimmedStatsRow.avg_ask_trimmed)}`}
                <br />
                {`Текущий bid/ask: ${trimmedStatsRow.latest_bid ?? '—'} / ${trimmedStatsRow.latest_ask ?? '—'} · ${trimmedStatsRow.latest_taken_at ? fmtDateTime(trimmedStatsRow.latest_taken_at) : '—'}`}
              </div>
            )}
          </Section>

          <Section
            header={(
              <div
                id="tracked-options-list"
                ref={anomalyOverviewTopRef}
                style={{ scrollMarginTop: 'var(--space-md)' }}
              >
                Список отслеживаемых опционов ({'>'} 30 дней)
              </div>
            )}
          >
            <div style={{ padding: 'var(--space-md) var(--space-lg)', display: 'grid', gap: 'var(--space-sm)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 'var(--space-sm)' }}>
                <input
                  type="date"
                  value={anomalyExpiryFromInput}
                  onChange={(e) => setAnomalyExpiryFromInput(e.target.value)}
                  style={{
                    width: '100%',
                    background: 'var(--tg-secondary-bg)',
                    color: 'var(--tg-text)',
                    border: 'none',
                    borderRadius: 'var(--radius-sm)',
                    padding: 'var(--space-xs) var(--space-sm)',
                    minHeight: 36,
                  }}
                />
                <input
                  type="date"
                  value={anomalyExpiryToInput}
                  onChange={(e) => setAnomalyExpiryToInput(e.target.value)}
                  style={{
                    width: '100%',
                    background: 'var(--tg-secondary-bg)',
                    color: 'var(--tg-text)',
                    border: 'none',
                    borderRadius: 'var(--radius-sm)',
                    padding: 'var(--space-xs) var(--space-sm)',
                    minHeight: 36,
                  }}
                />
              </div>
              <input
                value={anomalyTokenFilterInput}
                onChange={(e) => setAnomalyTokenFilterInput(e.target.value)}
                placeholder="Токен/база (например BTC, ETH, ADA_USDT)"
                autoCapitalize="characters"
                style={{
                  width: '100%',
                  background: 'var(--tg-secondary-bg)',
                  color: 'var(--tg-text)',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  padding: 'var(--space-xs) var(--space-sm)',
                  minHeight: 36,
                }}
              />
              {!!activeAnomalyExchanges.length && (
                <div style={{ display: 'grid', gap: 'var(--space-xs)' }}>
                  <span style={{ color: 'var(--tg-hint)', fontSize: 12 }}>Биржи:</span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-xs)' }}>
                    {activeAnomalyExchanges.map((exchange) => {
                      const selected = anomalySelectedExchanges.includes(exchange);
                      return (
                        <button
                          key={`overview-filter-${exchange}`}
                          type="button"
                          onClick={() => {
                            setAnomalyOverviewPage(0);
                            setAnomalySelectedExchanges((prev) => (
                              prev.includes(exchange)
                                ? prev.filter((item) => item !== exchange)
                                : [...prev, exchange]
                            ));
                          }}
                          style={{
                            border: 'none',
                            borderRadius: '999px',
                            background: selected ? 'var(--tg-button)' : 'var(--tg-secondary-bg)',
                            color: selected ? 'var(--tg-button-text)' : 'var(--tg-text)',
                            minHeight: 30,
                            padding: '0 var(--space-sm)',
                            cursor: 'pointer',
                            fontSize: '12px',
                          }}
                        >
                          {exchangeLabel(exchange)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
                {(['all', 'C', 'P'] as const).map((side) => (
                  <button
                    key={side}
                    type="button"
                    onClick={() => setAnomalyOptionTypeFilter(side)}
                    style={{
                      padding: '4px 12px',
                      borderRadius: 'var(--radius-sm)',
                      border: '0.5px solid var(--tg-separator)',
                      background: anomalyOptionTypeFilter === side ? 'var(--tg-button)' : 'var(--tg-secondary-bg)',
                      color: anomalyOptionTypeFilter === side ? 'var(--tg-button-text)' : 'var(--tg-text)',
                      fontSize: 'var(--font-sm)',
                      cursor: 'pointer',
                      fontWeight: anomalyOptionTypeFilter === side ? 600 : 400,
                    }}
                  >
                    {side === 'all' ? 'Все' : side === 'C' ? 'Call' : 'Put'}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setAnomalyExpiryFromInput('');
                    setAnomalyExpiryToInput('');
                    setAnomalyTokenFilterInput('');
                    setAnomalyTokenFilter('');
                    setAnomalyOptionTypeFilter('all');
                    setAnomalySelectedExchanges(activeAnomalyExchanges);
                    setAnomalyOverviewPage(0);
                  }}
                  style={{
                    marginLeft: 'auto',
                    border: 'none',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--tg-secondary-bg)',
                    color: 'var(--tg-text)',
                    minHeight: 30,
                    padding: '0 var(--space-sm)',
                    cursor: 'pointer',
                  }}
                >
                  Сбросить
                </button>
              </div>
            </div>
            {anomalyOverview.isLoading || anomalyOverviewCount.isLoading ? (
              <Spinner />
            ) : !anomalyOverviewByOption.length ? (
              <Empty icon="📈" title="Нет опционов" hint="В active_options пока нет опционов с экспирацией более 30 дней." />
            ) : (
              <>
                {anomalyOverviewByOption.map((entry) => {
                  const rowsWithData = entry.rows.filter(shouldRenderAnomalyExchangeRow);
                  const byExchange = new Map(rowsWithData.map((row) => [row.exchange, row]));
                  const orderedRows = activeAnomalyExchanges
                    .map((exchange) => byExchange.get(exchange))
                    .filter((row): row is ZetaAnomalyOverviewRow => Boolean(row));
                  const extraRows = rowsWithData
                    .filter((row) => row.exchange && !activeAnomalyExchanges.includes(row.exchange))
                    .sort((a, b) => exchangeLabel(a.exchange).localeCompare(exchangeLabel(b.exchange), 'ru'));
                  // Фильтр по биржам применяется на сервере (p_exchanges) — здесь только порядок отображения.
                  const visibleRows = [...orderedRows, ...extraRows];
                  return (
                    <div
                      key={entry.optionId}
                      style={{
                        padding: 'var(--space-md) var(--space-lg)',
                        borderBottom: '0.5px solid var(--tg-separator)',
                        display: 'grid',
                        gap: 'var(--space-sm)',
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>
                        {entry.optionId}
                        {` · ${optionBase(entry.optionId)}`}
                        {` · ${entry.expiryDays} дн.`}
                      </div>
                      {!visibleRows.length ? (
                        <div style={{ color: 'var(--tg-hint)', fontSize: 'var(--font-sm)' }}>Нет данных по биржам.</div>
                      ) : (
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', minWidth: 520, borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                              <tr>
                                <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '0.5px solid var(--tg-separator)' }}>Биржа</th>
                                <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '0.5px solid var(--tg-separator)' }}>Bid (trimmed)</th>
                                <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '0.5px solid var(--tg-separator)' }}>Ask (trimmed)</th>
                                <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '0.5px solid var(--tg-separator)' }}>Макс Bid</th>
                                <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '0.5px solid var(--tg-separator)' }}>Мин Ask</th>
                                <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '0.5px solid var(--tg-separator)' }}>Last Bid</th>
                                <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '0.5px solid var(--tg-separator)' }}>Last Ask</th>
                              </tr>
                            </thead>
                            <tbody>
                              {visibleRows.map((row) => (
                                <tr key={`${entry.optionId}-${row.exchange}`}>
                                  <td style={{ padding: '4px 8px', borderBottom: '0.5px solid var(--tg-separator)' }}>{exchangeLabel(row.exchange)}</td>
                                  <td style={{ padding: '4px 8px', borderBottom: '0.5px solid var(--tg-separator)' }}>
                                    <span style={{ color: isBidTrimmedHighlight(row.avg_bid_trimmed, row.max_bid) ? '#2ECC71' : 'inherit', fontWeight: isBidTrimmedHighlight(row.avg_bid_trimmed, row.max_bid) ? 600 : 400 }}>
                                      {fmtPrice(row.avg_bid_trimmed)}
                                    </span>
                                  </td>
                                  <td style={{ padding: '4px 8px', borderBottom: '0.5px solid var(--tg-separator)' }}>
                                    <span style={{ color: isAskTrimmedHighlight(row.avg_ask_trimmed, row.min_ask) ? '#2ECC71' : 'inherit', fontWeight: isAskTrimmedHighlight(row.avg_ask_trimmed, row.min_ask) ? 600 : 400 }}>
                                      {fmtPrice(row.avg_ask_trimmed)}
                                    </span>
                                  </td>
                                  <td style={{ padding: '4px 8px', borderBottom: '0.5px solid var(--tg-separator)' }}>
                                    {row.max_bid != null ? (
                                      <div style={{ display: 'grid', gap: 2 }}>
                                        <span>{fmtPrice(row.max_bid)}</span>
                                        {row.max_bid_taken_at ? (
                                          <span style={{ color: 'var(--tg-hint)', fontSize: 10 }}>{fmtDateTime(row.max_bid_taken_at)}</span>
                                        ) : null}
                                      </div>
                                    ) : '—'}
                                  </td>
                                  <td style={{ padding: '4px 8px', borderBottom: '0.5px solid var(--tg-separator)' }}>
                                    {row.min_ask != null ? (
                                      <div style={{ display: 'grid', gap: 2 }}>
                                        <span>{fmtPrice(row.min_ask)}</span>
                                        {row.min_ask_taken_at ? (
                                          <span style={{ color: 'var(--tg-hint)', fontSize: 10 }}>{fmtDateTime(row.min_ask_taken_at)}</span>
                                        ) : null}
                                      </div>
                                    ) : '—'}
                                  </td>
                                  <td style={{ padding: '4px 8px', borderBottom: '0.5px solid var(--tg-separator)' }}>
                                    {hasPositivePrice(row.last_bid) ? (
                                      <div style={{ display: 'grid', gap: 2 }}>
                                        <span>{fmtPrice(row.last_bid)}</span>
                                        {row.last_taken_at ? (
                                          <span style={{ color: 'var(--tg-hint)', fontSize: 10 }}>{fmtDateTime(row.last_taken_at)}</span>
                                        ) : null}
                                      </div>
                                    ) : '—'}
                                  </td>
                                  <td style={{ padding: '4px 8px', borderBottom: '0.5px solid var(--tg-separator)' }}>
                                    {hasPositivePrice(row.last_ask) ? (
                                      <div style={{ display: 'grid', gap: 2 }}>
                                        <span>{fmtPrice(row.last_ask)}</span>
                                        {row.last_taken_at ? (
                                          <span style={{ color: 'var(--tg-hint)', fontSize: 10 }}>{fmtDateTime(row.last_taken_at)}</span>
                                        ) : null}
                                      </div>
                                    ) : '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--space-sm) var(--space-lg)' }}>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setPendingAnomalyOverviewScroll(true);
                      setAnomalyOverviewPage((p) => Math.max(0, p - 1));
                    }}
                    disabled={anomalyOverviewPage <= 0}
                  >
                    Назад
                  </Button>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                    <span style={{ color: 'var(--tg-hint)', fontSize: 'var(--font-sm)' }}>
                      {`Страница ${anomalyOverviewPage + 1} из ${totalOverviewPages}`}
                    </span>
                    <input
                      value={anomalyOverviewPageInput}
                      onChange={(e) => {
                        setAnomalyOverviewPageInput(e.target.value);
                        setAnomalyOverviewPageInputError(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') goToAnomalyOverviewPage();
                      }}
                      inputMode="numeric"
                      placeholder="№"
                      style={{
                        width: 56,
                        background: 'var(--tg-secondary-bg)',
                        color: 'var(--tg-text)',
                        border: anomalyOverviewPageInputError ? '1px solid #E74C3C' : '0.5px solid var(--tg-separator)',
                        borderRadius: 'var(--radius-sm)',
                        padding: '4px 8px',
                        minHeight: 30,
                      }}
                    />
                    <Button variant="ghost" onClick={goToAnomalyOverviewPage}>Перейти</Button>
                  </div>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setPendingAnomalyOverviewScroll(true);
                      setAnomalyOverviewPage((p) => Math.min(totalOverviewPages - 1, p + 1));
                    }}
                    disabled={anomalyOverviewPage >= totalOverviewPages - 1}
                  >
                    Далее
                  </Button>
                </div>
              </>
            )}
          </Section>

          <Section header="Сохранённые индивидуальные пороги">
            {anomalyWatchers.isLoading ? (
              <Spinner />
            ) : !anomalyWatchers.data?.length ? (
              <Empty icon="🔔" title="Нет индивидуальных порогов" hint="Глобальные пороги уже работают для всех опционов с экспирацией > 30 дней." />
            ) : (
              anomalyWatchers.data.map((w) => (
                <Cell
                  key={w.id}
                  title={`${w.option_id} · ${exchangeLabel(w.exchange)}`}
                  subtitle={`Bid: ${w.bid_threshold_percent ?? 'global'}% · Ask: ${w.ask_threshold_percent ?? 'global'}%`}
                  after={(
                    <Button variant="destructive" onClick={() => void removeAnomalyWatcher(w.id)}>
                      ✕
                    </Button>
                  )}
                />
              ))
            )}
          </Section>
        </>
      )}
    </Page>
  );
}
